import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import { authService, normalizeAuthLoginPayload } from "@/lib/api/services";
import {
  clientGet,
  SESSION_LAYOUT_TIMEOUT_MS,
  ClientApiTimeoutError,
} from "@/lib/api/clientApi";
import { useCartStore } from "./cart";
import { setCookies, clearCookies, getCookies } from "@/lib/api/cookie-utils";
import { updateTokenCache } from "@/lib/api/client";
import { getErrorMessage } from "@/lib/utils/error-handler";
import { getUiLang } from "@/lib/utils/ui-lang";
import type { AuthSessionKind, User } from "@/types";

// Deduplicate `/api/session` calls across components on initial page load.
let sessionInFlight: Promise<void> | null = null;

function defaultLoginErrorMessage() {
  return getUiLang() === "ar" ? "فشل تسجيل الدخول" : "Login failed";
}

function defaultRegisterErrorMessage() {
  return getUiLang() === "ar" ? "فشل التسجيل" : "Registration failed";
}

function fastCheckoutSessionInfoMessage() {
  return getUiLang() === "ar"
    ? "تم تفعيل الدفع السريع فقط — لم يكتمل تسجيل الدخول. يمكنك إتمام التبرع الآن، وسجّل الدخول لاحقاً للوصول الكامل لحسابك."
    : "Quick checkout enabled — you are not fully signed in. You can complete this donation now; sign in later for full account access.";
}

interface AuthFeedback {
  open: boolean;
  type: "success" | "error" | "info";
  message: string;
}

interface AuthStore {
  isAuthenticated: boolean;
  /** `fast` after login-faster (checkout-only); `full` after normal login/register. */
  authSessionKind: AuthSessionKind;
  user: User | null;
  isLoading: boolean;
  /** True once `/api/session` has hydrated auth+cart for this page load. */
  sessionHydrated: boolean;
  /** Set when session fetch times out so UI can show "slow network" + retry. */
  sessionLoadError: "timeout" | null;
  authFeedback: AuthFeedback | null;

  // Actions
  clearSessionLoadError: () => void;
  showAuthFeedback: (
    message: string,
    type?: "success" | "error" | "info",
  ) => void;
  closeAuthFeedback: () => void;
  login: (
    mobile: string,
    password?: string,
  ) => Promise<{ success: boolean; message?: string }>;
  loginFaster: (mobile: string) => Promise<{
    success: boolean;
    message?: string;
    authSessionKind?: AuthSessionKind;
  }>;
  register: (data: {
    name?: string;
    mobile: string;
    email?: string;
    password: string;
    confirmPassword: string;
    associationId?: number;
  }) => Promise<{ success: boolean; message?: string }>;
  setPassword: (data: {
    mobile: string;
    password: string;
    confirmPassword: string;
  }) => Promise<{ success: boolean; message?: string }>;
        logout: (successMessage?: string) => Promise<void>;
  checkAuth: (options?: {
    manageLoading?: boolean;
    force?: boolean;
  }) => Promise<void>;
  setUser: (user: User | null) => void;
  clearAuth: () => Promise<void>;
  syncCartAfterAuth: () => Promise<void>;
}

export const useAuthStore = create<AuthStore>()(
  devtools(
    persist(
      (set, get) => ({
        isAuthenticated: false,
        authSessionKind: "full",
        user: null,
        isLoading: false,
        sessionHydrated: false,
        sessionLoadError: null,
        authFeedback: null,

        clearSessionLoadError: () =>
          set({ sessionLoadError: null }, false, "auth/clearSessionLoadError"),

        showAuthFeedback: (message, type = "success") =>
          set(
            { authFeedback: { open: true, type, message } },
            false,
            "auth/showFeedback",
          ),

        closeAuthFeedback: () =>
          set({ authFeedback: null }, false, "auth/closeFeedback"),

        login: async (mobile: string, password?: string) => {
          set({ isLoading: true }, false, "auth/login:start");
          try {
            if (!password) {
              set({ isLoading: false }, false, "auth/login:missingPassword");
              return { success: false, message: "Password is required" };
            }
            const response = await authService.login({
              username: mobile,
              password: password,
            });

            if (response.success && response.data) {
              const auth = normalizeAuthLoginPayload(response.data);
              if (!auth?.accessToken) {
                set({ isLoading: false }, false, "auth/login:noToken");
                return {
                  success: false,
                  message:
                    response.message ||
                    response.error ||
                    defaultLoginErrorMessage(),
                };
              }
              const { accessToken, refreshToken, user } = auth;
              // Store tokens in cookies
              await setCookies({
                accessToken,
                refreshToken: refreshToken || undefined,
                userData: user || undefined,
              });

              // Update token cache for faster access
              updateTokenCache(accessToken, refreshToken || null);

              set(
                {
                  isAuthenticated: true,
                  authSessionKind: "full",
                  user: user || null,
                  isLoading: true,
                },
                false,
                "auth/login:success",
              );

              // Fetch current user data if not provided (don't manage loading; we clear it after cart sync)
              if (!user) {
                await get().checkAuth({ manageLoading: false });
              }

              // Sync cart after login (keep loading until done)
              await get().syncCartAfterAuth();

              set({ isLoading: false }, false, "auth/login:ready");
              return { success: true };
            } else {
              set({ isLoading: false }, false, "auth/login:error");
              return {
                success: false,
                message:
                  response.message ||
                  response.error ||
                  defaultLoginErrorMessage(),
              };
            }
          } catch (error: unknown) {
            set({ isLoading: false }, false, "auth/login:exception");
            const message = getErrorMessage(error, defaultLoginErrorMessage());
            return { success: false, message };
          }
        },

        loginFaster: async (mobile: string) => {
          set({ isLoading: true }, false, "auth/loginFaster:start");
          try {
            const response = await authService.loginFaster(mobile);

            if (response.success && response.data) {
              const auth = normalizeAuthLoginPayload(response.data);
              if (!auth?.accessToken) {
                set({ isLoading: false }, false, "auth/loginFaster:noToken");
                return {
                  success: false,
                  message:
                    response.message ||
                    response.error ||
                    defaultLoginErrorMessage(),
                };
              }
              const { accessToken, refreshToken, user } = auth;
              const sessionKind: AuthSessionKind = "fast";
              await setCookies({
                accessToken,
                refreshToken: refreshToken || undefined,
                userData: user || undefined,
              });

              updateTokenCache(accessToken, refreshToken || null);

              set(
                {
                  isAuthenticated: true,
                  authSessionKind: sessionKind,
                  user: user || null,
                  isLoading: true,
                },
                false,
                "auth/loginFaster:success",
              );

              if (!user) {
                await get().checkAuth({ manageLoading: false });
              }

              // Sync cart after login (keep loading until done)
              await get().syncCartAfterAuth();

              set({ isLoading: false }, false, "auth/loginFaster:ready");
              get().showAuthFeedback(fastCheckoutSessionInfoMessage(), "info");
              return { success: true, authSessionKind: sessionKind };
            } else {
              set({ isLoading: false }, false, "auth/loginFaster:error");
              return {
                success: false,
                message:
                  response.message ||
                  response.error ||
                  defaultLoginErrorMessage(),
              };
            }
          } catch (error: unknown) {
            set({ isLoading: false }, false, "auth/loginFaster:exception");
            const message = getErrorMessage(error, defaultLoginErrorMessage());
            return { success: false, message };
          }
        },

        register: async (data) => {
          set({ isLoading: true }, false, "auth/register:start");
          try {
            // Validate passwords match
            if (data.password !== data.confirmPassword) {
              set(
                { isLoading: false },
                false,
                "auth/register:passwordMismatch",
              );
              return { success: false, message: "Passwords do not match" };
            }

            const response = await authService.register({
              mobile: data.mobile,
              password: data.password,
              name: data.name,
              email: data.email,
              associationId: data.associationId,
            });

            if (response.success && response.data) {
              const accessToken = response.data.accessToken;
              const refreshToken = response.data.refreshToken || null;
              const user = response.data.user;

              // Store tokens in cookies
              const cookiesStored = await setCookies({
                accessToken,
                refreshToken: refreshToken || undefined,
                userData:
                  user && Object.keys(user).length > 0 ? user : undefined,
              });

              // Optional dev-only diagnostics (never log token values or user objects)
              if (process.env.NODE_ENV !== "production") {
                const { verifyCookies } =
                  await import("@/lib/api/cookie-utils");
                const cookieVerification = await verifyCookies();
                console.debug("[auth] register cookie write", {
                  cookiesStored,
                  cookieVerification,
                  hasUserData: !!(user && Object.keys(user).length > 0),
                });
              }

              // Update token cache for faster access
              updateTokenCache(accessToken, refreshToken);

              set(
                {
                  isAuthenticated: true,
                  authSessionKind: "full",
                  user: user && Object.keys(user).length > 0 ? user : null,
                  isLoading: true,
                },
                false,
                "auth/register:success",
              );

              // Fetch current user data if not provided or empty (don't manage loading; we clear it after cart sync)
              const hasUserData = user && Object.keys(user).length > 0;
              if (!hasUserData) {
                await get().checkAuth({ manageLoading: false });
              }

              // Sync cart after registration (keep loading until done)
              await get().syncCartAfterAuth();

              set({ isLoading: false }, false, "auth/register:ready");
              return { success: true };
            } else {
              set({ isLoading: false }, false, "auth/register:error");
              return {
                success: false,
                message:
                  response.message ||
                  response.error ||
                  defaultRegisterErrorMessage(),
              };
            }
          } catch (error: unknown) {
            set({ isLoading: false }, false, "auth/register:exception");
            const message = getErrorMessage(
              error,
              defaultRegisterErrorMessage(),
            );
            return { success: false, message };
          }
        },

        setPassword: async (data) => {
          set({ isLoading: true }, false, "auth/setPassword:start");
          try {
            // Validate passwords match
            if (data.password !== data.confirmPassword) {
              set(
                { isLoading: false },
                false,
                "auth/setPassword:passwordMismatch",
              );
              return { success: false, message: "Passwords do not match" };
            }

            const response = await authService.setPassword({
              mobile: data.mobile,
              password: data.password,
            });

            if (response.success && response.data) {
              const accessToken = response.data.accessToken;
              const refreshToken = response.data.refreshToken || null;
              const user = response.data.user;

              // Store tokens in cookies
              await setCookies({
                accessToken,
                refreshToken: refreshToken || undefined,
                userData:
                  user && Object.keys(user).length > 0 ? user : undefined,
              });

              // Update token cache
              updateTokenCache(accessToken, refreshToken);

              set(
                {
                  isAuthenticated: true,
                  authSessionKind: "full",
                  user: user && Object.keys(user).length > 0 ? user : null,
                  isLoading: true,
                },
                false,
                "auth/setPassword:success",
              );

              // Sync cart after setting password (keep loading until done)
              await get().syncCartAfterAuth();

              set({ isLoading: false }, false, "auth/setPassword:ready");
              return { success: true };
            } else {
              set({ isLoading: false }, false, "auth/setPassword:error");
              return {
                success: false,
                message:
                  response.message ||
                  response.error ||
                  "Failed to set password",
              };
            }
          } catch (error: unknown) {
            set({ isLoading: false }, false, "auth/setPassword:exception");
            const message = getErrorMessage(error, "Failed to set password");
            return { success: false, message };
          }
        },

        logout: async (successMessage?: string) => {
          try {
            await authService.logout();
          } catch (error) {
            console.error("Logout API error:", error);
          } finally {
            await clearCookies();
            await get().clearAuth();
            if (successMessage) {
              get().showAuthFeedback(successMessage);
            }
          }
        },

        checkAuth: async (options?: {
          manageLoading?: boolean;
          force?: boolean;
        }) => {
          const manageLoading = options?.manageLoading !== false;
          const force = options?.force === true;

          if (force) {
            set({ sessionLoadError: null }, false, "auth/checkAuth:clearError");
          }

          if (!force && get().sessionHydrated) return;

          if (manageLoading) {
            set({ isLoading: true }, false, "auth/checkAuth:start");
          }

          // Only allow one in-flight session hydration at a time.
          if (!force && sessionInFlight) {
            await sessionInFlight;
            if (manageLoading) {
              set({ isLoading: false }, false, "auth/checkAuth:done");
            }
            return;
          }

          const run = async () => {
            const cookieSnapshot = await getCookies();
            const token = cookieSnapshot.accessToken;
            const refresh = cookieSnapshot.refreshToken;

            try {
              const json = await clientGet<{
                success?: boolean;
                user?: User | null;
                items?: unknown[];
                message?: string;
              }>("/api/session", undefined, {
                timeoutMs: SESSION_LAYOUT_TIMEOUT_MS,
              });

              const items = Array.isArray(json.items)
                ? (json.items as unknown[])
                : [];
              // Hydrate cart from the same endpoint (guests + logged-in).
              useCartStore.setState(
                {
                  items: items as unknown as ReturnType<
                    typeof useCartStore.getState
                  >["items"],
                  isHydrated: true,
                  isLoading: false,
                  // Surface a partial guest→DB merge so checkout can explain an
                  // otherwise-empty cart instead of blaming a missing amount.
                  lastError: json.message,
                },
                false,
                "cart/hydrateFromSession",
              );

              if (json.user) {
                await setCookies({ userData: json.user });
                updateTokenCache(token, refresh);
                // A quick-donation ("fast") session stays checkout-only even when it
                // resolves to a real account; only an explicit login upgrades it.
                const prevKind = get().authSessionKind;
                const authSessionKind = prevKind === "fast" ? "fast" : "full";
                set(
                  {
                    isAuthenticated: true,
                    authSessionKind,
                    user: json.user,
                    sessionHydrated: true,
                    sessionLoadError: null,
                  },
                  false,
                  "auth/checkAuth:session:authenticated",
                );
              } else {
                // Keep existing auth cookies/token cache when profile payload is temporarily unavailable.
                // Clearing tokens here can cause cart loss on refresh after a transient `/api/session` failure.
                set(
                  {
                    isAuthenticated: !!token,
                    user: token ? get().user : null,
                    sessionHydrated: true,
                    sessionLoadError: null,
                  },
                  false,
                  "auth/checkAuth:session:guest",
                );
              }
            } catch (error) {
              // Session hydration failed — still mark the cart hydrated so the
              // cart UI recovers (drawer fetches items on open) instead of
              // spinning on the loading state forever.
              useCartStore.setState(
                { isHydrated: true, isLoading: false },
                false,
                "cart/hydrateFromSession:failed",
              );
              if (error instanceof ClientApiTimeoutError) {
                set(
                  {
                    isAuthenticated: false,
                    user: null,
                    sessionHydrated: true,
                    sessionLoadError: "timeout",
                  },
                  false,
                  "auth/checkAuth:session:timeout",
                );
                return;
              }
              console.error("Session hydration failed:", error);
              set(
                {
                  isAuthenticated: false,
                  user: null,
                  sessionHydrated: true,
                  sessionLoadError: null,
                },
                false,
                "auth/checkAuth:session:error",
              );
            }
          };

          const p = run().finally(() => {
            if (!force) sessionInFlight = null;
          });

          if (!force) sessionInFlight = p;
          await p;

          if (manageLoading) {
            set({ isLoading: false }, false, "auth/checkAuth:done");
          }
        },

        setUser: (user) => {
          set({ user, isAuthenticated: !!user }, false, "auth/setUser");
        },

        clearAuth: async () => {
          // Clear cookies
          await clearCookies();

          // Clear token cache
          updateTokenCache(null, null);

          set(
            {
              isAuthenticated: false,
              authSessionKind: "full",
              user: null,
              sessionHydrated: false,
            },
            false,
            "auth/clearAuth",
          );

          // Clear cart so UI and server session reflect logged-out state
          useCartStore.getState().clearCart();
        },

        syncCartAfterAuth: async () => {
          // ASP.NET-like behavior is handled server-side by Next `/api/cart`:
          // - guest cart is stored in server session
          // - when authenticated, API migrates guest cart to DB cart
          // Client only needs to rehydrate the cart.
          try {
            // Prefer the merged session endpoint so user+cart are consistent after auth.
            await get().checkAuth({ manageLoading: false, force: true });
          } catch (error) {
            console.error("Failed to hydrate cart after auth:", error);
          }
        },
      }),
      {
        name: "jood-auth-storage",
        version: 3,
        partialize: (state) => ({
          user: state.user,
          isAuthenticated: state.isAuthenticated,
          authSessionKind: state.authSessionKind,
        }),
        migrate: (persisted, version) => {
          let state = persisted as unknown;
          // v1 persisted tokens in localStorage; v2 intentionally drops them.
          if (version < 2 && state && typeof state === "object") {
            const p = state as Partial<AuthStore> & {
              accessToken?: unknown;
              refreshToken?: unknown;
            };
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { accessToken, refreshToken, ...rest } = p;
            state = rest;
          }
          if (version < 3 && state && typeof state === "object") {
            const s = state as Partial<AuthStore>;
            if (s.authSessionKind !== "fast" && s.authSessionKind !== "full") {
              state = { ...s, authSessionKind: "full" as const };
            }
          }
          return state as AuthStore;
        },
      },
    ),
    {
      name: "jood/auth",
      enabled: process.env.NODE_ENV === "development",
    },
  ),
);

